import { useEffect, useState, type ReactNode } from 'react';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { RouterProvider, type createHashRouter } from 'react-router';
import type { Clock } from '@sl/core';
import { ApiClientProvider, type ApiClientSource } from './api/ApiClientProvider';
import { isLiveDatasetError, NoLiveDatasetScreen } from './api/liveLook';
import { ClockProvider } from './clock/ClockProvider';
import { AppConfigProvider, DEMO_APP_CONFIG } from './configContext';
import type { AppConfig } from './config';
import { LiveRegion } from './live/LiveRegion';
import { StaleBuildScreen } from './StaleBuildScreen';
import { isStaleBuildSeen, subscribeStaleBuild } from './staleBuild';
import { ThemeProvider } from './theme/ThemeProvider';
import type { ThemeController } from './theme/theme';
import { ToastProvider } from '../ui/Toast/ToastProvider';

// The provider stack (ARCH §9.1): Config → Clock → Theme → ApiClient → QueryClient → Toast → router. The
// aria-live region sits beside the router so it survives navigation; the toast stack (T-035) announces through
// it. `main.tsx` builds the real values; tests pass fakes.

export interface AppProvidersProps {
  clock: Clock;
  theme: ThemeController;
  client: ApiClientSource;
  queryClient: QueryClient;
  /** How this build runs (T-283). Omitted = a plain demo build, which is what every test wants by default. */
  config?: AppConfig;
  children: ReactNode;
}

export function AppProviders({
  clock,
  theme,
  client,
  queryClient,
  config = DEMO_APP_CONFIG,
  children,
}: AppProvidersProps) {
  return (
    <AppConfigProvider value={config}>
      <ClockProvider clock={clock}>
        <ThemeProvider controller={theme}>
          <ApiClientProvider client={client}>
            <QueryClientProvider client={queryClient}>
              <ToastProvider>{children}</ToastProvider>
              <LiveRegion />
            </QueryClientProvider>
          </ApiClientProvider>
        </ThemeProvider>
      </ClockProvider>
    </AppConfigProvider>
  );
}

export type AppRouter = ReturnType<typeof createHashRouter>;

/**
 * BUG-054 — has this tab's build been replaced on the server, in a way a reload has already failed to fix?
 *
 * The guard (`app/staleBuild.ts`) hears every `vite:preloadError`, including the ones React Router never
 * turns into a route error: a static host answers a missing `assets/route-*.js` with `index.html`, the
 * browser refuses the MIME type, and the router renders an empty outlet with a console warning. A blank page
 * inside a working shell is the worst of the three outcomes, and it is the one the founder actually saw.
 */
function useStaleBuild(): boolean {
  const [stale, setStale] = useState(isStaleBuildSeen);
  useEffect(() => subscribeStaleBuild(() => setStale(true)), []);
  return stale;
}

/**
 * T-283 — did the ONE client fail to arrive because there is no pulled dataset beside this app?
 *
 * `?dataset=live` makes `createAppApiClient` fetch `live/` from this origin, and on a build that has never been
 * pulled into there is nothing to fetch. That rejection reaches a screen two ways and both are handled: the
 * route error boundary catches it when a route asks for the client, and this hook catches it for the whole app —
 * which matters because the welcome route never asks, so without this the address would simply appear to work
 * until you clicked into it, and the rejection would sit unhandled in the console.
 *
 * Only that one failure is claimed. Any other rejected client is left to the boundary, which knows how to say
 * "this page couldn't load" with the API's own words.
 */
function useNoLiveDataset(client: ApiClientSource): boolean {
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    let live = true;
    void Promise.resolve(client).then(null, (error: unknown) => {
      if (live && isLiveDatasetError(error)) setMissing(true);
    });
    return () => {
      live = false;
    };
  }, [client]);
  return missing;
}

export function App(
  props: Omit<AppProvidersProps, 'children'> & { createRouter: () => AppRouter },
) {
  const { createRouter, ...providers } = props;
  // Created once for the life of the app; StrictMode's double render must not build two routers.
  const [router] = useState(createRouter);
  const stale = useStaleBuild();
  const noLiveDataset = useNoLiveDataset(props.client);
  return (
    <AppProviders {...providers}>
      {stale ? (
        <StaleBuildScreen />
      ) : noLiveDataset ? (
        <NoLiveDatasetScreen />
      ) : (
        <RouterProvider router={router} />
      )}
    </AppProviders>
  );
}
